import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useConfirm from '../hooks/useConfirm';
import ConfirmModal from './ConfirmModal';

const UserManagement = ({ currentUser }) => {
    const [users, setUsers] = useState([]);
    const [formData, setFormData] = useState({ full_name: '', email: '', password: '', confirmPassword: '', role: 'staff' });
    
    // --- NEW: EDIT MODAL STATES ---
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editUser, setEditUser] = useState({ user_id: '', full_name: '', email: '', password: '', confirmPassword: '', role: '' });
    
    // --- EMAIL VERIFICATION STATES FOR EDIT ---
    const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [verificationMessage, setVerificationMessage] = useState('');
    const [pendingEditUser, setPendingEditUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { confirm, confirmState, handleClose } = useConfirm();

    // --- ROLE FILTERING ---
    const [roleFilter, setRoleFilter] = useState('all');

    const fetchUsers = async () => {
        const res = await axios.get('/api/users');
        setUsers(res.data);
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate passwords match
        if (formData.password !== formData.confirmPassword) {
            alert("Passwords do not match.");
            return;
        }
        
        // Determine confirmation message based on role
        let confirmMessage = `Create new ${formData.role} account for ${formData.full_name}?\n\n`;
        if (formData.role === 'admin') {
            confirmMessage += "⚠️ This account will have the SAME EXACT permissions as your account (Full Control).";
        } else if (formData.role === 'staff') {
            confirmMessage += "This account will have upload and edit permissions only.";
        }
        
        const ok = await confirm({
            title: 'Register User',
            message: confirmMessage,
            confirmText: 'Register',
            cancelText: 'Cancel'
        });
        if (!ok) return;
        
        try {
            await axios.post('/api/users', { ...formData, adminId: currentUser.user_id });
            setFormData({ full_name: '', email: '', password: '', confirmPassword: '', role: 'staff' });
            fetchUsers();
            alert("User Registered!");
        } catch (err) { alert("Error: Email already exists"); }
    };

    // --- NEW: HANDLE USER UPDATE ---
    const handleUpdateUser = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setVerificationMessage('');
        
        // Validate passwords match if password is provided
        if (editUser.password && editUser.password !== editUser.confirmPassword) {
            alert("Passwords do not match.");
            setIsLoading(false);
            return;
        }
        
        // Find the original user to check what changed
        const originalUser = users.find(u => u.user_id === editUser.user_id);
        const isEditingOwnAccount = editUser.user_id === currentUser.user_id;
        const roleChanged = originalUser && originalUser.role !== editUser.role;
        
        // Prevent user from changing their own role
        if (isEditingOwnAccount && roleChanged) {
            alert("You cannot change your own role.");
            setIsLoading(false);
            return;
        }
        
        // Build confirmation message based on role
        let confirmMessage = `Update account for ${editUser.full_name}?\n\n`;
        if (editUser.role === 'admin') {
            confirmMessage += "⚠️ This account will have the SAME EXACT permissions as your account (Full Control).";
        } else if (editUser.role === 'staff') {
            confirmMessage += "This account will have upload and edit permissions only.";
        }
        
        const ok = await confirm({
            title: 'Confirm User Update',
            message: confirmMessage,
            confirmText: 'Update',
            cancelText: 'Cancel'
        });
        if (!ok) {
            setIsLoading(false);
            return;
        }
        
        const nameChanged = originalUser && originalUser.full_name !== editUser.full_name;
        const emailChanged = originalUser && originalUser.email !== editUser.email;
        const passwordChanged = editUser.password && editUser.password.trim() !== '';
        
        // If only role changed, update directly
        if (!nameChanged && !emailChanged && !passwordChanged && roleChanged) {
            try {
                await axios.put(`/api/users/${editUser.user_id}`, {
                    ...editUser,
                    adminId: currentUser.user_id
                });
                setIsEditModalOpen(false);
                setIsVerifyingEmail(false);
                setVerificationCode('');
                fetchUsers();
                alert("User updated successfully!");
                setIsLoading(false);
            } catch (err) {
                alert("Error updating user.");
                setIsLoading(false);
            }
            return;
        }
        
        // Otherwise, request verification code for name/email/password changes
        try {
            await axios.post(
                `/api/users/${editUser.user_id}/request-email-verification`,
                { 
                    newEmail: emailChanged ? editUser.email : null,
                    currentEmail: originalUser.email
                }
            );
            setPendingEditUser(editUser);
            setIsVerifyingEmail(true);
            setVerificationMessage(`A verification code has been sent to ${emailChanged ? editUser.email : originalUser.email}. Please enter it below to confirm the changes.`);
            setIsLoading(false);
        } catch (err) {
            setVerificationMessage('Error sending verification code. Please try again.');
            setIsLoading(false);
        }
    };
    
    const handleVerifyEmailForEdit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setVerificationMessage('');
        
        const ok = await confirm({
            title: 'Confirm Changes',
            message: `Confirm changes for ${pendingEditUser.full_name}?`,
            confirmText: 'Confirm',
            cancelText: 'Cancel'
        });
        if (!ok) {
            setIsLoading(false);
            return;
        }
        
        try {
            const originalUser = users.find(u => u.user_id === pendingEditUser.user_id);
            await axios.post(
                `/api/users/${pendingEditUser.user_id}/verify-email`,
                {
                    newEmail: pendingEditUser.email !== originalUser.email ? pendingEditUser.email : null,
                    verifyCurrent: originalUser.email,
                    verificationCode: verificationCode,
                    full_name: pendingEditUser.full_name,
                    password: pendingEditUser.password
                }
            );
            
            setIsEditModalOpen(false);
            setIsVerifyingEmail(false);
            setVerificationCode('');
            setPendingEditUser(null);
            fetchUsers();
            alert("User updated successfully!");
            setIsLoading(false);
        } catch (err) {
            setVerificationMessage(err.response?.data || "Failed to verify changes. Please try again.");
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const warning = "This user will lose access to their account and their permissions. This action cannot be undone.";
        const ok = await confirm({
            title: 'Delete User',
            message: `Delete this user account?\n\n${warning}`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            danger: true
        });
        if (!ok) return;
        await axios.delete(`/api/users/${id}?adminId=${currentUser.user_id}`);
        fetchUsers();
    };

    // --- FILTER USERS BY ROLE ---
    const getFilteredUsers = () => {
        if (roleFilter === 'all') return users;
        return users.filter(u => u.role === roleFilter);
    };

    return (
        <div style={{ padding: '30px' }}>
            <h2 style={{ marginBottom: '20px' }}>User Access Management</h2>
            <div style={managementLayout}>
                
                {/* Registration Form */}
                <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #eee' }}>
                    <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>Register New Staff</h3>
                    <form onSubmit={handleSubmit}>
                        <input type="text" placeholder="Full Name" value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} required style={uInput} />
                        <input type="email" placeholder="Email Address" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required style={uInput} />
                        <input type="password" placeholder="Initial Password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} required style={uInput} />
                        <input type="password" placeholder="Confirm Password" value={formData.confirmPassword} onChange={e => setFormData({...formData, confirmPassword: e.target.value})} required style={uInput} />
                        <select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} style={uInput}>
                            <option value="staff">Staff (Upload/Edit only)</option>
                            <option value="admin">Admin (Full Control)</option>
                        </select>
                        <button type="submit" style={addBtn}>Create Account</button>
                    </form>
                </div>

                {/* Users List */}
                <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                    {/* Filter Controls */}
                    <div style={{ padding: '15px', background: '#f8f9fa', borderBottom: '1px solid #eee', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#555', alignSelf: 'center' }}>Filter:</span>
                        <button 
                            onClick={() => setRoleFilter('all')}
                            style={{
                                ...filterBtn,
                                background: roleFilter === 'all' ? '#343a40' : '#e9ecef',
                                color: roleFilter === 'all' ? 'white' : '#333'
                            }}
                        >
                            All Users ({users.length})
                        </button>
                        <button 
                            onClick={() => setRoleFilter('admin')}
                            style={{
                                ...filterBtn,
                                background: roleFilter === 'admin' ? '#343a40' : '#e9ecef',
                                color: roleFilter === 'admin' ? 'white' : '#333'
                            }}
                        >
                            Admin ({users.filter(u => u.role === 'admin').length})
                        </button>
                        <button 
                            onClick={() => setRoleFilter('staff')}
                            style={{
                                ...filterBtn,
                                background: roleFilter === 'staff' ? '#343a40' : '#e9ecef',
                                color: roleFilter === 'staff' ? 'white' : '#333'
                            }}
                        >
                            Staff ({users.filter(u => u.role === 'staff').length})
                        </button>
                    </div>
                    <div style={usersTableWrapper}>
                        <table style={usersTable}>
                            <thead style={{ background: '#343a40', color: 'white' }}>
                                <tr>
                                    <th style={p12}>Name & Email</th>
                                    <th style={p12}>Role</th>
                                    <th style={p12}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {getFilteredUsers().map(user => (
                                    <tr key={user.user_id} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={nameCell}>
                                            <div style={{ fontWeight: 'bold' }}>{user.full_name}</div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                                        </td>
                                        <td style={roleCell}>
                                            <span style={{ 
                                                padding: '4px 10px', 
                                                borderRadius: '20px', 
                                                fontSize: '11px', 
                                                background: user.role === 'admin' ? '#007bff' : '#6c757d', 
                                                color: 'white',
                                                textTransform: 'uppercase'
                                            }}>{user.role}</span>
                                        </td>
                                        <td style={actionsCell}>
                                            <div style={{ display: 'flex', gap: '8px', whiteSpace: 'nowrap' }}>
                                                <button 
                                                    onClick={() => { setEditUser({ ...user, password: '', confirmPassword: '' }); setIsEditModalOpen(true); }} 
                                                    style={editBtn}
                                                >Edit</button>
                                                {user.user_id !== currentUser.user_id && (
                                                    <button onClick={() => handleDelete(user.user_id)} style={delBtn}>Remove</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* --- EDIT USER MODAL --- */}
            {isEditModalOpen && (
                <div style={modalOverlay}>
                    <div style={modalContent}>
                        {!isVerifyingEmail ? (
                            <>
                                <h3>Edit User Account</h3>
                                <form onSubmit={handleUpdateUser}>
                                    <label style={labelStyle}>Full Name</label>
                                    <input style={uInput} type="text" value={editUser.full_name} onChange={e => setEditUser({...editUser, full_name: e.target.value})} required />
                                    
                                    <label style={labelStyle}>Email Address</label>
                                    <input style={uInput} type="email" value={editUser.email} onChange={e => setEditUser({...editUser, email: e.target.value})} required />
                                    
                                    <label style={labelStyle}>New Password (leave blank to keep current)</label>
                                    <input style={uInput} type="password" value={editUser.password} onChange={e => setEditUser({...editUser, password: e.target.value})} placeholder="••••••••" />
                                    
                                    <label style={labelStyle}>Confirm Password</label>
                                    <input style={uInput} type="password" value={editUser.confirmPassword} onChange={e => setEditUser({...editUser, confirmPassword: e.target.value})} placeholder="••••••••" />
                                    
                                    <label style={labelStyle}>Role</label>
                                    <select 
                                        style={uInput} 
                                        value={editUser.role} 
                                        onChange={e => setEditUser({...editUser, role: e.target.value})}
                                        disabled={editUser.user_id === currentUser.user_id}
                                        title={editUser.user_id === currentUser.user_id ? "You cannot change your own role" : ""}
                                    >
                                        <option value="staff">Staff</option>
                                        <option value="admin">Admin</option>
                                    </select>

                                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                        <button type="submit" style={addBtn} disabled={isLoading}>
                                            {isLoading ? 'Processing...' : 'Save Changes'}
                                        </button>
                                        <button type="button" onClick={() => setIsEditModalOpen(false)} style={{ ...addBtn, background: '#6c757d' }}>Cancel</button>
                                    </div>
                                </form>
                            </>
                        ) : (
                            <>
                                <h3>Verify Email Change</h3>
                                <form onSubmit={handleVerifyEmailForEdit}>
                                    <div style={verificationBox}>
                                        <p style={{ color: '#666', marginBottom: '15px', fontSize: '14px' }}>
                                            {verificationMessage}
                                        </p>
                                        
                                        <label style={labelStyle}>Verification Code</label>
                                        <input 
                                            style={uInput} 
                                            type="text" 
                                            placeholder="Enter 6-digit code"
                                            maxLength="6"
                                            value={verificationCode} 
                                            onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                                            required
                                        />
                                        
                                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                            <button 
                                                type="submit" 
                                                style={addBtn}
                                                disabled={isLoading || verificationCode.length !== 6}
                                            >
                                                {isLoading ? 'Verifying...' : 'Verify Email'}
                                            </button>
                                            <button 
                                                type="button" 
                                                onClick={() => {
                                                    setIsVerifyingEmail(false);
                                                    setVerificationCode('');
                                                    setVerificationMessage('');
                                                    setPendingEditUser(null);
                                                }}
                                                style={{ ...addBtn, background: '#6c757d' }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
            <ConfirmModal state={confirmState} onClose={handleClose} />
        </div>
    );
};

// --- STYLES ---
const uInput = { width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' };
const addBtn = { flex: 1, padding: '12px', background: '#28a745', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const delBtn = { padding: '6px 12px', background: '#fff', border: '1px solid #dc3545', color: '#dc3545', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' };
const editBtn = { padding: '6px 12px', background: '#ffc107', border: 'none', color: '#000', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' };
const filterBtn = { padding: '6px 12px', background: '#e9ecef', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', transition: 'all 0.2s ease' };
const managementLayout = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '40px' };
const usersTableWrapper = { overflowX: 'auto' };
const usersTable = { width: '100%', minWidth: '620px', borderCollapse: 'collapse' };
const p12 = { padding: '15px', textAlign: 'left' };
const nameCell = { ...p12, minWidth: '240px' };
const roleCell = { ...p12, minWidth: '110px' };
const actionsCell = { ...p12, minWidth: '160px' };
const labelStyle = { fontSize: '12px', fontWeight: 'bold', color: '#555', marginBottom: '5px', display: 'block' };
const modalOverlay = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const modalContent = { background: 'white', padding: '30px', borderRadius: '12px', width: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' };
const verificationBox = { padding: '15px', background: '#f0f8ff', border: '2px solid #b91c1c', borderRadius: '6px', marginTop: '10px' };

// Override style for disabled select
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.innerHTML = `
        select:disabled {
            background-color: #e9ecef;
            color: #6c757d;
            cursor: not-allowed;
            opacity: 0.7;
        }
    `;
    if (document.head) {
        document.head.appendChild(style);
    }
}

export default UserManagement;

